/**
 * THE CTS DIMENSIONS: what the timing points did, and where they stood when they fired.
 *
 * <pre>
 *    5  upside reversals by zone      count and success rate, Zone 1 / 2 / 3
 *    6  downside reversals by zone    the same, the other way
 *    7  CTS with RSI divergence       frequency and win rate, long and short
 *    9  distance from equator         does being further out make a reversion likelier
 *   13  CTS x RSI synthesis           what the two say together, in words
 * </pre>
 *
 * <h2>THE BASELINE IS THE WHOLE POINT, and it is computed here</h2>
 *
 * A 54% success rate is meaningless alone. SPY closes higher ten bars later about 60% of the
 * time, so a long signal winning 54% of the time is WORSE than buying at random — and a table of
 * success rates with no base rate beside them invites exactly the opposite conclusion. Every rate
 * below is reported against the base rate measured on the same bars over the same horizon, and
 * the dashboard prints the EDGE (signal minus baseline) rather than only the rate.
 *
 * <p>This is the same discipline the forward-return tests in CLAUDE.md already record, and the
 * reason those tests overturned three separate entry vocabularies that looked profitable.
 *
 * <h2>UNRESOLVED is not a failure</h2>
 *
 * A signal fired eight bars before the clicked bar has no ten-bar outcome. It is counted apart
 * and excluded from both numerator and denominator, because folding it in would make every recent
 * signal look bad for the sole reason that time has not passed — which is the trap the Timing
 * Engine's annual summary already names.
 *
 * <h2>The windows OVERLAP, and that overstates every t-statistic</h2>
 *
 * Signals cluster, so two points four bars apart share most of a ten-bar window. Nothing here
 * reports a p-value for that reason; the Wilson interval is reported instead, and the comparison
 * that survives overlap is the one against the baseline on the same bars.
 */

import { detectPivots, PIVOT_HIGH, PIVOT_LOW } from './pivots';
import { BAR_CLOSE, heightsFromEquator, zoneOf, ZONES } from './analyticaGeometry';
import { mean, pearson, rate, versusBaseline, wilson } from './analyticaStats';

/** Bars ahead a signal is judged over. Ten sessions — two trading weeks. */
export const DEFAULT_HORIZON = 10;

/** How far either side of a CTS point an RSI divergence may sit and still be concurrent. */
export const DEFAULT_DIVERGENCE_WINDOW = 5;

/** Distance-from-equator tiers for dimension 9, in band heights. */
export const DISTANCE_EDGES = [0.5, 1.0, 2.0];
export const DISTANCE_LABELS = ['inside 0.5h', '0.5–1h', '1–2h', 'beyond 2h'];

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Every RSI divergence in the scanned range, from the chart's OWN pivots and the chart's OWN RSI.
 *
 * <pre>
 *   BEARISH   price makes a HIGHER high while RSI makes a LOWER high
 *   BULLISH   price makes a LOWER low   while RSI makes a HIGHER low
 * </pre>
 *
 * <p>Consecutive same-type pivots only. Comparing a high against the high three swings back finds
 * far more divergences and most of them are between points no reader would pair.
 *
 * <p>`detectPivots` rather than a swing detector of its own, so the divergences, the Fibonacci
 * swings and anything else on this chart cannot disagree about what a turn is. Its comparison is
 * STRICT on both sides, so a flat top yields no pivot — a shelf of equal highs is not two swings.
 *
 * <p>Confirmed LATE by construction: a pivot needs `strength` bars after it, so a divergence is
 * only knowable `strength` bars later. That is a property of a swing, not a flaw, and it is why
 * the concurrence window below is symmetric rather than backwards-only.
 *
 * @returns {Array<{index:number, type:'BULLISH'|'BEARISH', from:number, priceΔ:number, rsiΔ:number}>}
 */
export function rsiDivergences(ohlc, rsi, upToIndex, strength) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const series = Array.isArray(rsi) ? rsi : [];
  const last = n(upToIndex);
  if (last === null || bars.length === 0 || series.length === 0) return [];

  const scope = bars.slice(0, last + 1);
  const { all } = detectPivots(scope, strength);
  const out = [];

  [PIVOT_HIGH, PIVOT_LOW].forEach((type) => {
    const same = all.filter(p => p.type === type);
    for (let i = 1; i < same.length; i++) {
      const a = same[i - 1];
      const b = same[i];
      const ra = n(series[a.index]);
      const rb = n(series[b.index]);
      if (ra === null || rb === null) continue;

      const priceUp = b.price > a.price;
      const rsiUp = rb > ra;
      // A divergence needs the two to DISAGREE. Equal on either side is not a disagreement, so
      // strict comparisons on both — the same rule the pivot detector itself applies.
      if (type === PIVOT_HIGH && priceUp && rb < ra) {
        out.push({ index: b.index, from: a.index, type: 'BEARISH',
          priceDelta: b.price - a.price, rsiDelta: rb - ra });
      }
      if (type === PIVOT_LOW && !priceUp && b.price < a.price && rsiUp) {
        out.push({ index: b.index, from: a.index, type: 'BULLISH',
          priceDelta: b.price - a.price, rsiDelta: rb - ra });
      }
    }
  });

  return out.sort((a, b) => a.index - b.index);
}

/**
 * The base rate: how often price simply closes higher (or lower) `horizon` bars on.
 *
 * <p>Measured over EVERY bar of the scan that has a full horizon, which is the population a
 * signal is drawn from. Measuring it over the signal bars alone would compare the signals against
 * themselves.
 */
export function baseRates(ohlc, upToIndex, horizon) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const last = n(upToIndex);
  const h = Math.max(1, Math.floor(n(horizon) || DEFAULT_HORIZON));
  if (last === null) return { up: null, down: null, n: 0, horizon: h };

  let up = 0;
  let total = 0;
  for (let i = 0; i + h <= last; i++) {
    const a = n(bars[i]?.[BAR_CLOSE]);
    const b = n(bars[i + h]?.[BAR_CLOSE]);
    if (a === null || b === null) continue;
    total += 1;
    if (b > a) up += 1;
  }
  if (total === 0) return { up: null, down: null, n: 0, horizon: h };
  return { up: up / total, down: 1 - up / total, n: total, horizon: h };
}

/**
 * Dimensions 5, 6, 7, 9 and 13.
 *
 * @param {object} input
 * @param {Array<object>} input.points  `{date, direction}` read off the CTS overlay on the chart
 * @param {Array<string>} input.dates   the chart's axis, for resolving a point to a bar
 * @param {Array<number[]>} input.ohlc
 * @param {Array<number>} input.rsi
 * @param {Array<object|null>} input.sectionByBar which channel governs each bar
 * @param {number} input.upToIndex
 */
export function analyseCts({
  points, dates, ohlc, rsi, sectionByBar, upToIndex,
  horizon = DEFAULT_HORIZON, divergenceWindow = DEFAULT_DIVERGENCE_WINDOW, pivotStrength,
}) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const axis = Array.isArray(dates) ? dates : [];
  const last = n(upToIndex);
  const h = Math.max(1, Math.floor(n(horizon) || DEFAULT_HORIZON));
  const base = baseRates(bars, last, h);

  // The axis position of every date, once. `indexOf` per point is O(points x bars), which on a
  // 33-year chart with 400 points is eight million string comparisons for no reason.
  const at = new Map();
  axis.forEach((d, i) => { if (!at.has(d)) at.set(d, i); });

  const divergences = rsiDivergences(bars, rsi, last, pivotStrength);
  const divAt = new Map();
  divergences.forEach((d) => {
    for (let k = d.index - divergenceWindow; k <= d.index + divergenceWindow; k++) {
      if (!divAt.has(k)) divAt.set(k, d.type);
    }
  });

  const rows = [];
  (Array.isArray(points) ? points : []).forEach((p) => {
    if (!p || typeof p.date !== 'string') return;
    const i = at.has(p.date) ? at.get(p.date) : -1;
    if (i < 0 || last === null || i > last) return;
    const close = n(bars[i]?.[BAR_CLOSE]);
    if (close === null) return;

    const section = sectionByBar?.[i] || null;
    const direction = p.direction === 'down' ? 'down' : 'up';
    const resolvedAt = i + h;
    const resolved = resolvedAt <= last;
    const future = resolved ? n(bars[resolvedAt]?.[BAR_CLOSE]) : null;

    const distance = section ? heightsFromEquator(close, section) : null;
    const movedToward = (resolved && section && distance !== null && future !== null)
      ? Math.abs(heightsFromEquator(future, section)) < Math.abs(distance)
      : null;

    rows.push({
      index: i,
      date: p.date,
      direction,
      close,
      rsi: n(rsi?.[i]),
      zone: section ? zoneOf(close, section) : null,
      category: section ? section.category : null,
      distance,
      absDistance: distance === null ? null : Math.abs(distance),
      resolved,
      // Success is DIRECTIONAL: an upside reversal is right when price is higher, a downside one
      // when it is lower. Not "did it move", which both sides would pass half the time.
      success: resolved && future !== null
        ? (direction === 'up' ? future > close : future < close)
        : null,
      returnPct: resolved && future !== null && close !== 0
        ? ((future - close) / close) * (direction === 'up' ? 1 : -1) * 100
        : null,
      meanReverted: movedToward,
      divergence: divAt.get(i) || null,
      // A divergence AGREES with a point when it points the same way: a bullish divergence
      // beside an upside reversal. One of the opposite sense beside it is a disagreement, and
      // counting it as concurrence would make the dimension measure proximity rather than
      // confluence.
      divergenceAgrees: divAt.get(i)
        ? ((divAt.get(i) === 'BULLISH') === (direction === 'up'))
        : false,
    });
  });

  const up = rows.filter(r => r.direction === 'up');
  const down = rows.filter(r => r.direction === 'down');

  return {
    horizon: h,
    baseline: base,
    total: rows.length,
    unresolved: rows.filter(r => !r.resolved).length,
    divergenceCount: divergences.length,
    upsideByZone: byZone(up, base.up),
    downsideByZone: byZone(down, base.down),
    withDivergence: divergenceSplit(rows, base),
    distanceVsReversion: distanceAnalysis(rows),
    synthesis: synthesise(rows, divergences, base),
    rows,
  };
}

/** Dimensions 5 and 6 — count and success rate per zone, each against the base rate. */
function byZone(rows, baseline) {
  const out = ZONES.map((zone) => {
    const inZone = rows.filter(r => r.zone === zone);
    const done = inZone.filter(r => r.resolved);
    const wins = done.filter(r => r.success).length;
    return {
      zone,
      count: inZone.length,
      unresolved: inZone.length - done.length,
      ...rate(wins, done.length),
      edge: done.length > 0 && baseline !== null ? (wins / done.length) - baseline : null,
      verdict: versusBaseline(wins, done.length, baseline),
      meanReturnPct: mean(done.map(r => r.returnPct)),
      ci: wilson(wins, done.length),
    };
  });
  // A point with no channel in force belongs to no zone, and silently dropping it would make the
  // three zones sum to fewer points than the chart shows.
  const unzoned = rows.filter(r => r.zone === null);
  return {
    zones: out,
    unzoned: unzoned.length,
    total: rows.length,
  };
}

/** Dimension 7 — with an agreeing divergence against without one, both sides. */
function divergenceSplit(rows, base) {
  const side = (direction, baseline) => {
    const all = rows.filter(r => r.direction === direction && r.resolved);
    const withDiv = all.filter(r => r.divergenceAgrees);
    const without = all.filter(r => !r.divergenceAgrees);
    const tally = (set) => {
      const wins = set.filter(r => r.success).length;
      return {
        ...rate(wins, set.length),
        edge: set.length > 0 && baseline !== null ? (wins / set.length) - baseline : null,
        verdict: versusBaseline(wins, set.length, baseline),
        meanReturnPct: mean(set.map(r => r.returnPct)),
        ci: wilson(wins, set.length),
      };
    };
    return {
      direction,
      concurrence: rate(withDiv.length, all.length),
      with: tally(withDiv),
      without: tally(without),
      // The LIFT is the comparison the dimension exists for, and it is the difference of two
      // rates rather than either alone: a divergence filter is worth having only if the signals
      // it keeps beat the ones it discards.
      lift: withDiv.length > 0 && without.length > 0
        ? (withDiv.filter(r => r.success).length / withDiv.length)
          - (without.filter(r => r.success).length / without.length)
        : null,
    };
  };
  return { up: side('up', base.up), down: side('down', base.down) };
}

/**
 * Dimension 9 — does standing further from the midline make a reversion likelier?
 *
 * <p>Reported two ways because a correlation alone hides a non-monotone relationship, which is
 * the shape this question most plausibly has: mean reversion could strengthen out to the channel
 * edge and then collapse once price has broken out of it entirely.
 *
 * <p>The correlation is POINT-BISERIAL — a continuous distance against a 0/1 outcome — which is
 * just Pearson with a binary variable and is read the same way.
 */
function distanceAnalysis(rows) {
  const usable = rows.filter(r => r.resolved && r.absDistance !== null && r.meanReverted !== null);
  const buckets = DISTANCE_LABELS.map((label, i) => {
    const lo = i === 0 ? 0 : DISTANCE_EDGES[i - 1];
    const hi = i < DISTANCE_EDGES.length ? DISTANCE_EDGES[i] : Infinity;
    const set = usable.filter(r => r.absDistance >= lo && r.absDistance < hi);
    const hits = set.filter(r => r.meanReverted).length;
    return { label, from: lo, to: hi, ...rate(hits, set.length), ci: wilson(hits, set.length) };
  });
  return {
    n: usable.length,
    correlation: pearson(
      usable.map(r => r.absDistance),
      usable.map(r => (r.meanReverted ? 1 : 0)),
    ),
    overall: rate(usable.filter(r => r.meanReverted).length, usable.length),
    buckets,
  };
}

/** Dimension 13 — the two readings held against each other, as numbers a sentence can be built on. */
function synthesise(rows, divergences, base) {
  const resolved = rows.filter(r => r.resolved);
  const withRsi = resolved.filter(r => r.rsi !== null);
  return {
    points: rows.length,
    divergences: divergences.length,
    concurrent: rows.filter(r => r.divergenceAgrees).length,
    conflicting: rows.filter(r => r.divergence && !r.divergenceAgrees).length,
    meanRsiUp: mean(rows.filter(r => r.direction === 'up').map(r => r.rsi)),
    meanRsiDown: mean(rows.filter(r => r.direction === 'down').map(r => r.rsi)),
    // Does the RSI READING at the signal bar say anything about the outcome, independently of
    // divergence? A flat zero here is the useful answer as often as not: it says the level is
    // not what carries whatever the divergence does.
    rsiVsSuccess: pearson(
      withRsi.map(r => (r.direction === 'up' ? r.rsi : 100 - r.rsi)),
      withRsi.map(r => (r.success ? 1 : 0)),
    ),
    baseline: base,
  };
}

export default analyseCts;
